import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GameAction, GameConfig, GameState } from '@/engine/types';
import { newGame, reduce } from '@/engine/reducer';

export type GameRole = 'local' | 'host' | 'client';

/**
 * Returns true if the action was handled elsewhere (e.g. forwarded to a host).
 * Installed by the networking layer.
 */
export type ActionInterceptor = (action: GameAction) => boolean;

interface GameStore {
  game: GameState | null;
  /** Wall-clock time of the last change to `game` (for "last played" labels). */
  updatedAt: number;
  role: GameRole;
  /** Player ids this device controls. Empty = every human seat (hot-seat). */
  localPlayerIds: string[];
  interceptor: ActionInterceptor | null;

  start: (config: GameConfig) => GameState;
  /** Apply an action to local state without interception. */
  apply: (action: GameAction) => void;
  /** Route an action through the interceptor (network) or apply locally. */
  dispatch: (action: GameAction) => void;
  /** Replace state wholesale (network sync). */
  setGame: (game: GameState | null) => void;
  setRole: (role: GameRole, localPlayerIds?: string[]) => void;
  setInterceptor: (fn: ActionInterceptor | null) => void;
  /** Clear the current game. */
  quit: () => void;
}

export const useGame = create<GameStore>()(
  persist(
    (set, get) => ({
      game: null,
      updatedAt: 0,
      role: 'local',
      localPlayerIds: [],
      interceptor: null,

      start: config => {
        const game = newGame(config);
        set({ game, updatedAt: Date.now() });
        return game;
      },
      apply: action => {
        const { game } = get();
        if (!game) return;
        const next = reduce(game, action);
        if (next !== game) set({ game: next, updatedAt: Date.now() });
      },
      dispatch: action => {
        const { interceptor } = get();
        if (interceptor && interceptor(action)) return;
        get().apply(action);
      },
      setGame: game => set({ game, updatedAt: Date.now() }),
      setRole: (role, localPlayerIds = []) => set({ role, localPlayerIds }),
      setInterceptor: fn => set({ interceptor: fn }),
      quit: () =>
        set({
          game: null,
          role: 'local',
          localPlayerIds: [],
          interceptor: null,
        }),
    }),
    {
      name: 'yazzi.game.v1',
      // Local and hosted games survive reload (the host can reopen its room);
      // a client's copy is re-sent by the host.
      partialize: s => ({
        game: s.role === 'client' ? null : s.game,
        updatedAt: s.updatedAt,
      }),
    }
  )
);

/** Whether this device may take back the most recent score. */
export function canLocalUndoScore(
  store: Pick<GameStore, 'game' | 'role' | 'localPlayerIds'>
): boolean {
  const { game, role, localPlayerIds } = store;
  if (!game || game.phase !== 'playing' || !game.scoreUndo) return false;
  const scorer = game.players.find(p => p.id === game.scoreUndo?.playerId);
  if (!scorer || scorer.kind === 'ai') return false;
  if (role === 'local') return scorer.kind === 'human';
  return localPlayerIds.includes(scorer.id);
}

/** Whether this device may act for the player whose turn it is. */
export function isLocalTurn(
  store: Pick<GameStore, 'game' | 'role' | 'localPlayerIds'>
): boolean {
  const { game, role, localPlayerIds } = store;
  if (!game || game.phase !== 'playing') return false;
  const current = game.players[game.turn];
  if (!current) return false;
  if (current.kind === 'ai') return false;
  if (role === 'local') return current.kind === 'human';
  return localPlayerIds.includes(current.id);
}
